import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { v4 as uuid } from 'uuid';
import { useDispatch } from 'react-redux';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import StructureThumbnailContainer from '@Containers/StructureThumbnail';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';
import parseV3000SumFormula from '@Utils/React/MolFiletoFormula';
import { getQueryDisplayState } from './queryTextUtils';

function getStructureForDialog(historyItem) {
  return historyItem?.quickSearchItem?.structure || historyItem?.structure || historyItem?.structures?.[0]?.structure;
}

function getStructureNameForDialog(structure) {
  if (!structure) {
    return undefined;
  }

  const formula = parseV3000SumFormula(structure);
  if (!formula) {
    return undefined;
  }

  return {
    name: formula,
    isLinearFormula: true,
  };
}

function QueryItemContent({ historyItem, onExpandedChange = () => {} }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const dispatch = useDispatch();
  const structureForDialog = getStructureForDialog(historyItem);
  const structureNameForDialog = getStructureNameForDialog(structureForDialog);
  const { hasQuickSearchStructure, hasStructureListItem, showToggle, showStructure, displayedText } = getQueryDisplayState(
    historyItem,
    isExpanded
  );
  const isTextOnlyCollapsed = !isExpanded && !showStructure;

  // Unique id for aria-controls
  const queryTextId = `query-text-${historyItem.resultName || uuid()}`;

  const toggleExpanded = () => {
    const nextIsExpanded = !isExpanded;
    setIsExpanded(nextIsExpanded);
    onExpandedChange(nextIsExpanded);
  };

  // Both thumbnails open the same dialog; only the image source and test hook differ.
  const structureButtonProps = {
    type: 'secondary',
    'aria-label': 'Expand substance structure in modal',
    className: 'query-text-structure-button',
    onClick: () => dispatch(openStructureDialog(uuid(), structureForDialog, structureNameForDialog)),
  };

  return (
    <div>
      <div>
        {historyItem.batchSearch && (
          <div>
            <ButtonWithIcon
              data-e2e="history-batch-search-execution-details"
              type="link"
              sprite="ChevronRight"
              iconRight
              aria-label="Show execution details for batch search"
              onClick={() => {}}>
              Execution details
            </ButtonWithIcon>
          </div>
        )}
        <div
          className={cn({
            'u-els-display-flex query-text-container': !isExpanded,
            'query-text-container--text-only u-els-margin-bottom-1x1o2': isTextOnlyCollapsed,
          })}>
          <span id={queryTextId} className={cn('query-text-content', { 'query-text-content--collapsed': !isExpanded })}>
            {displayedText}
          </span>
          {showStructure && hasQuickSearchStructure && (
            <Button data-e2e="expanstructure-img" {...structureButtonProps}>
              <StructureThumbnailContainer
                enableThumbnail
                structure={historyItem?.quickSearchItem?.structure}
                structureName={historyItem?.quickSearchItem?.content}
                thumbnailType="history"
              />
            </Button>
          )}
          {showStructure && hasStructureListItem && (
            <Button data-e2e="structure-icon-button" {...structureButtonProps}>
              <StructureThumbnailContainer
                enableThumbnail
                structure={historyItem?.structure || historyItem?.structures?.[0]?.structure}
                thumbnailType="history"
              />
            </Button>
          )}
        </div>
        {showToggle && (
          <div className="query-text-toggle">
            <ButtonWithIcon
              data-e2e="history-query-text-toggle"
              size="small"
              iconRight
              sprite={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              type="link"
              linkIconSize="xs"
              aria-expanded={isExpanded}
              aria-controls={queryTextId}
              onClick={toggleExpanded}>
              {isExpanded ? 'Collapse' : 'Show all'}
            </ButtonWithIcon>
          </div>
        )}
      </div>
    </div>
  );
}

QueryItemContent.propTypes = {
  historyItem: PropTypes.object.isRequired,
  onExpandedChange: PropTypes.func,
};

export default QueryItemContent;
